<div class="container-fluid" style="margin-top: 15px">
  <div *ngIf="loading"><i class="fas fa-spinner fa-pulse"></i> Loading...</div>

  <p class="text-muted">
    Move the Tables Definition between environments, or build it up in stages. Items are matched <b>by name</b>, an import only
    <b>adds and updates</b> (nothing is ever deleted), anything that overwrites an existing item is shown to you first, and an import is applied
    all-or-nothing.
  </p>

  <!-- ================================ EXPORT ================================ -->
  <mat-expansion-panel [expanded]="true" style="margin-bottom: 15px">
    <mat-expansion-panel-header>
      <mat-panel-title><b>Export</b></mat-panel-title>
      <mat-panel-description>Build a file from this environment's definition</mat-panel-description>
    </mat-expansion-panel-header>

    <div class="row" style="margin-top: 10px">
      <div class="col-md-6">
        <mat-form-field class="full-width">
          <mat-label>Find a table</mat-label>
          <input matInput [(ngModel)]="tableSearch" autocomplete="off" />
        </mat-form-field>
        <div style="margin-bottom: 6px">
          <button mat-stroked-button (click)="selectVisible()">Select {{ tableSearch ? "shown" : "all" }}</button>&nbsp;
          <button mat-stroked-button (click)="clearTables()" [disabled]="selectedTables.size === 0">Clear</button>
          <span class="text-muted" style="margin-left: 10px">{{ selectedTables.size === 0 ? "Nothing selected: every table is exported" : selectedTables.size + " selected" }}</span>
        </div>
        <div style="max-height: 320px; overflow-y: auto; border: 1px solid #ddd; border-radius: 4px; padding: 6px 10px">
          <div *ngFor="let t of visibleTables">
            <mat-checkbox [checked]="selectedTables.has(t.tableName)" (change)="toggleTable(t.tableName, $event.checked)">
              {{ t.tableName }}
              <span class="text-muted">({{ columnCount(t.admTableId) }} columns)</span>
              <span *ngIf="t.retired" class="badge badge-secondary" style="margin-left: 4px">retired</span>
            </mat-checkbox>
          </div>
          <div *ngIf="visibleTables.length === 0" class="text-muted">No tables match.</div>
        </div>
      </div>

      <div class="col-md-6">
        <div><mat-checkbox [(ngModel)]="includeRelatedTables">Also include related tables</mat-checkbox></div>
        <div class="text-muted" style="margin: 0 0 8px 28px; font-size: 90%">
          Tables on the other end of the selected tables' relationships and flattened-column paths. Without this they are referenced by name, and must
          already exist where the file is imported.
        </div>
        <div><mat-checkbox [(ngModel)]="includeRetired">Include retired items</mat-checkbox></div>
        <div style="margin-top: 8px"><mat-checkbox [(ngModel)]="includeGroups">Include Groups</mat-checkbox></div>
        <div class="text-muted" style="margin: 0 0 4px 28px; font-size: 90%">
          Each Group's tables and allowed columns, by name. Plan-type assignments are not exported (plan types differ per environment).
        </div>
        <div *ngIf="includeGroups" style="margin: 0 0 8px 28px; max-height: 160px; overflow-y: auto">
          <div class="text-muted" style="font-size: 90%">{{ selectedGroups.size === 0 ? "No Group selected: all Groups are exported" : selectedGroups.size + " selected" }}</div>
          <div *ngFor="let g of groups">
            <mat-checkbox [checked]="selectedGroups.has(g.name)" (change)="toggleGroup(g.name, $event.checked)">{{ g.name }}</mat-checkbox>
          </div>
          <div *ngIf="groups.length === 0" class="text-muted">There are no Groups.</div>
        </div>

        <div style="margin-top: 15px">
          <button mat-raised-button class="piButton" (click)="export()" [disabled]="exporting || loading">
            <i class="fas fa-download"></i> Export {{ exportScope }}
          </button>
          <span *ngIf="exporting" style="margin-left: 10px"><i class="fas fa-spinner fa-pulse"></i> Building...</span>
        </div>
        <div *ngIf="lastExport" class="alert alert-success" style="margin-top: 10px">
          Downloaded. The file contains {{ exportSummary(lastExport) }}.
        </div>
      </div>
    </div>
  </mat-expansion-panel>

  <!-- ================================ IMPORT ================================ -->
  <mat-expansion-panel [expanded]="true">
    <mat-expansion-panel-header>
      <mat-panel-title><b>Import</b></mat-panel-title>
      <mat-panel-description>Preview and apply a file</mat-panel-description>
    </mat-expansion-panel-header>

    <!-- STEP 1: FILE -->
    <div *ngIf="!reviewing">
      <adm-steps [steps]="['File', 'Preview', 'Confirm', 'Result']" [current]="0"></adm-steps>
      <div
        (drop)="onDrop($event)" (dragover)="onDragOver($event)" (dragleave)="dragging = false"
        style="border: 2px dashed #999; border-radius: 6px; padding: 28px; text-align: center"
        [style.background]="dragging ? '#e3f2fd' : 'transparent'"
      >
        <div><i class="fas fa-file-upload" style="font-size: 28px; color: #666"></i></div>
        <div style="margin: 8px 0">Drop an exported <b>.json</b> file here, or</div>
        <input #fileInput type="file" accept=".json,application/json" (change)="onFileInput($event)" style="display: none" />
        <button mat-raised-button class="piButton" (click)="fileInput.click()">Choose a file</button>
      </div>

      <div *ngIf="fileName" style="margin-top: 12px">
        <b>{{ fileName }}</b>
        <div *ngFor="let p of fileProblems" class="alert alert-danger" style="margin: 8px 0 0">{{ p }}</div>
        <div *ngIf="document && fileProblems.length === 0" class="text-muted" style="margin-top: 4px">
          Exported {{ document.exportedUtc | date: "medium" }}<span *ngIf="document.exportedFromEnvironment"> from {{ document.exportedFromEnvironment }}</span>.
          Contains {{ fileSummary }}.
        </div>
      </div>

            <div style="margin-top: 15px">
        <button mat-raised-button class="piButton" (click)="startReview()" [disabled]="!document || fileProblems.length > 0">Preview import</button>
        <span class="text-muted" style="margin-left: 10px">Nothing is written until you confirm.</span>
      </div>
    </div>

    <!-- STEPS 2-4: preview, confirm, result (shared with the "Reuse flattened columns" screen) -->
    <admin-adm-transfer-review
      *ngIf="reviewing && document"
      [document]="document"
      firstStep="File"
      finishLabel="Import another file"
      (back)="reviewing = false"
      (finished)="resetImport()"
      (imported)="onImported()"
      (openValidation)="openValidation.emit()"
    ></admin-adm-transfer-review>
  </mat-expansion-panel>
</div>
